import { useState } from 'react'
import {
  Sparkles,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Building,
  Mail,
  User,
  Users,
  Calendar,
  Clock,
} from 'lucide-react'
import { sendContactInformation } from '../../../../services/publicServices/sendContactInformation'
const DemoBookingForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    agencyName: '',
    clientRange: '10 a 25 clientes',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const clientRangeOptions = [
    '1 a 10 clientes',
    '10 a 25 clientes',
    '+25 clientes',
  ]
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.fullName || !formData.workEmail || !formData.agencyName) {
      return
    }
    setIsSubmitting(true)
    try {
      const response = await sendContactInformation({
        Nombre: formData.fullName,
        'Email corporativo': formData.workEmail,
        Agencia: formData.agencyName,
        'Clientes activos': formData.clientRange,
        Origen: 'Landing Agencias - Acceso Prioritario',
      }, 'empresa@hutrit.com')
      if (response && response.status === 200) {
        setIsSubmitted(true)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSubmitting(false)
    }
  }
  return (
    <section
      id="demo-form"
      className="py-24 relative bg-[#011e1b] bg-tactical-grid border-t border-[#00FF9D]/15"
    >
      {/* Background ambient neon glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full blur-[140px] pointer-events-none opacity-20"
        style={{
          background: 'radial-gradient(circle, #00FF9D 0%, #05332E 100%)',
        }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#05332E] border border-[#00FF9D]/30 text-xs font-mono text-[#00FF9D] uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Acceso Prioritario para Agencias
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
            Despliega tu equipo de IA en tu agencia{' '}
            <span className="text-[#00FF9D]">este mismo mes</span>
          </h2>
          <p className="text-base sm:text-lg text-[#CBD5E1]">
            Completa el formulario para agendar una demo personalizada y ver la
            Oficina Virtual en acción.
          </p>
        </div>

        {/* Card Form */}
        <div className="rounded-3xl p-6 sm:p-10 bg-[#031E1B] border-2 border-[#00FF9D]/30 shadow-[0_20px_50px_rgba(0,0,0,0.6)] backdrop-blur-xl relative overflow-hidden">
          {isSubmitted ? (
            <div className="py-12 px-4 text-center max-w-lg mx-auto">
              <div className="w-16 h-16 rounded-full bg-[#00FF9D]/20 border border-[#00FF9D] text-[#00FF9D] flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(0,255,157,0.4)]">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                ¡Solicitud Recibida con Éxito!
              </h3>
              <p className="text-[#CBD5E1] text-sm mb-6 leading-relaxed">
                Gracias,{' '}
                <strong className="text-white">{formData.fullName}</strong>.
                Hemos reservado una demostración técnica exclusiva para{' '}
                <strong className="text-[#00FF9D]">
                  {formData.agencyName}
                </strong>
                . Te hemos enviado los detalles de acceso y agenda a{' '}
                <span className="text-white font-mono">
                  {formData.workEmail}
                </span>
                .
              </p>

              <div className="p-4 rounded-xl bg-[#05332E] border border-[#00FF9D]/30 text-left text-xs space-y-2 mb-6 font-mono text-[#CBD5E1]">
                <div className="flex items-center gap-2 text-white font-bold">
                  <Calendar className="w-4 h-4 text-[#00FF9D]" />
                  <span>Duración de la sesión: 15 minutos</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#00FF9D]" />
                  <span>
                    Contenido: Demostración de Oficina Virtual + Flujos de 10
                    Clientes
                  </span>
                </div>
              </div>

              <button
                onClick={() => setIsSubmitted(false)}
                className="px-6 py-2.5 rounded-full bg-[#05332E] hover:bg-[#0A3F39] text-[#CBD5E1] text-xs font-mono border border-[#00FF9D]/20 transition-colors"
              >
                Enviar otra solicitud
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Campo 1: Nombre completo */}
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-xs font-mono font-bold uppercase tracking-wider text-[#CBD5E1] mb-2 flex items-center gap-1.5"
                  >
                    <User className="w-3.5 h-3.5 text-[#00FF9D]" />
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    required
                    placeholder="Ej. Carlos Mendoza"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    className="w-full px-4 py-3.5 rounded-xl bg-[#012622] border border-[#00FF9D]/25 focus:border-[#00FF9D] text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00FF9D] transition-all text-sm"
                  />
                </div>

                {/* Campo 2: Email corporativo */}
                <div>
                  <label
                    htmlFor="workEmail"
                    className="block text-xs font-mono font-bold uppercase tracking-wider text-[#CBD5E1] mb-2 flex items-center gap-1.5"
                  >
                    <Mail className="w-3.5 h-3.5 text-[#00FF9D]" />
                    Email corporativo *
                  </label>
                  <input
                    type="email"
                    id="workEmail"
                    required
                    placeholder="carlos@tuagencia.com"
                    value={formData.workEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, workEmail: e.target.value })
                    }
                    className="w-full px-4 py-3.5 rounded-xl bg-[#012622] border border-[#00FF9D]/25 focus:border-[#00FF9D] text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00FF9D] transition-all text-sm"
                  />
                </div>
              </div>

              {/* Campo 3: Nombre de la Agencia */}
              <div>
                <label
                  htmlFor="agencyName"
                  className="block text-xs font-mono font-bold uppercase tracking-wider text-[#CBD5E1] mb-2 flex items-center gap-1.5"
                >
                  <Building className="w-3.5 h-3.5 text-[#00FF9D]" />
                  Nombre de la Agencia *
                </label>
                <input
                  type="text"
                  id="agencyName"
                  required
                  placeholder="Ej. Nexus Growth Agency"
                  value={formData.agencyName}
                  onChange={(e) =>
                    setFormData({ ...formData, agencyName: e.target.value })
                  }
                  className="w-full px-4 py-3.5 rounded-xl bg-[#012622] border border-[#00FF9D]/25 focus:border-[#00FF9D] text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00FF9D] transition-all text-sm"
                />
              </div>

              {/* Campo 4 (Selector / Radio buttons): Número de clientes activos actualmente */}
              <div>
                <label className="block text-xs font-mono font-bold uppercase tracking-wider text-[#CBD5E1] mb-3 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-[#00FF9D]" />
                  Número de clientes activos actualmente *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {clientRangeOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center justify-center p-3.5 rounded-xl border cursor-pointer transition-all text-sm font-medium ${
                        formData.clientRange === option
                          ? 'bg-[#05332E] border-[#00FF9D] text-white shadow-[0_0_15px_rgba(0,255,157,0.25)]'
                          : 'bg-[#012622]/60 border-[#00FF9D]/15 text-[#CBD5E1] hover:bg-[#05332E]/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="clientRange"
                        value={option}
                        checked={formData.clientRange === option}
                        onChange={() =>
                          setFormData({ ...formData, clientRange: option })
                        }
                        className="sr-only"
                      />
                      <span className="flex items-center gap-2">
                        <span
                          className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                            formData.clientRange === option
                              ? 'border-[#00FF9D] bg-[#00FF9D]'
                              : 'border-gray-500'
                          }`}
                        >
                          {formData.clientRange === option && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#012622]" />
                          )}
                        </span>
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Botón de envío (Verde Menta Neón) */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00FF9D] hover:bg-[#00E676] text-[#012622] font-extrabold text-base transition-all duration-200 shadow-[0_0_30px_rgba(0,255,157,0.4)] hover:shadow-[0_0_40px_rgba(0,255,157,0.6)] cursor-pointer active:scale-[0.99] disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-[#012622] border-t-transparent animate-spin" />
                    Procesando reserva...
                  </span>
                ) : (
                  <>
                    <span>Solicitar Demo Personalizada</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Security and privacy note */}
              <div className="text-center text-xs font-mono text-[#CBD5E1]/60 flex items-center justify-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#00FF9D]" />
                <span>
                  Respuesta en menos de 4 horas laborables • Confidencialidad
                  NDA garantizada
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}
export { DemoBookingForm }
